import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import {
	IAnswerForStats,
	IAssessment,
	IAssessmentFilters,
	ICreateAssessmentRequest,
	ICreateOrgInviteRequest,
	IInvite,
	IInviteAction,
	IOrganizationUser,
	IPaginatedAssessmentsResponse,
	IPeriod,
	IPosition,
	IPositionRole,
	IPositionsFilters,
	IPositionTrajectory,
	IPublicOrganization,
	IRole,
	ISaveAnswersRequest,
	IUser,
	IUserPositionAssessmentFilters,
	NextpeakClient,
} from '../services/nextpeakClient'
import { toast } from 'react-toastify'
import { UpdateUserDto } from '../services/nextpeakClient/endpoints/updateUser'
import { NEXTPEAK_API_URL } from '../config.ts'
import { NextpeakPublicClient } from '../services/nextpeakPublicClient'
import { IPaginatedAssessmentFilters } from '../services/nextpeakClient/endpoints/getPaginatedAssessments.ts'
import { IGetAnswersFilter } from '../services/nextpeakClient/endpoints/getAnswers.ts'
import { IOrganizationUsersFilter } from '../services/nextpeakClient/endpoints/getOrganizationUsers.v2.ts'

// TODO: Add this to the client
const fetchUserOrganizations = async (token: string) => {
	const response = await fetch(`${NEXTPEAK_API_URL}/api/auth/organizations`, {
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
	})
	if (!response.ok) {
		throw new Error('Network response was not ok')
	}
	return response.json()
}

export const useOrganizationsByToken = (token: string | undefined) => {
	return useQuery({
		queryKey: ['AuthUserOrganization', token],
		queryFn: () => token && fetchUserOrganizations(token),
		enabled: !!token, // Only run this query if token is not null or undefined
	})
}

export const useGetOrganizationUsers = <TData = IUser[], TError = unknown>(
	client: NextpeakClient,
	params: { organizationId: string; managerId?: string; includes?: string[] },
	options?: Omit<UseQueryOptions<IUser[], TError, TData>, 'queryKey'>
) => {
	const { organizationId, managerId, includes } = params
	const opts = options || {}
	return useQuery<IUser[], TError, TData>({
		queryKey: ['GetOrganizationUsers', organizationId, managerId, includes],
		queryFn: () => client.getOrganizationUsers(organizationId, managerId, includes),
		...opts,
	})
}

export const useGetOrganizationUsersV2 = <TData = IOrganizationUser[], TError = unknown>(
	client: NextpeakClient,
	params: { organizationId: string; filters: IOrganizationUsersFilter },
	options?: Omit<UseQueryOptions<IOrganizationUser[], TError, TData>, 'queryKey'>
) => {
	const { organizationId, filters } = params
	const opts = options || {}
	return useQuery<IOrganizationUser[], TError, TData>({
		queryKey: ['GetOrganizationUsersV2', organizationId, filters],
		queryFn: () => client.getOrganizationUsersV2(organizationId, filters),
		...opts,
	})
}

export const useGetOrganizationPeriodsV2 = <TData = IPeriod[], TError = unknown>(
	client: NextpeakClient,
	params: { organizationId: string },
	options?: Omit<UseQueryOptions<IPeriod[], TError, TData>, 'queryKey'>
) => {
	const { organizationId } = params
	const opts = options || {}
	return useQuery<IPeriod[], TError, TData>({
		queryKey: ['GetOrganizationPeriodsV2', organizationId],
		queryFn: () => client.getOrganizationPeriodsV2(organizationId),
		...opts,
	})
}

export const useCreateOrgInvite = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: (inviteData: ICreateOrgInviteRequest) => client.createOrganizationInvite(inviteData),
		onSuccess: (res) => {
			console.log(res)
			toast.success('Invite created')
		},
		onError: (error) => {
			// Optionally handle errors here, like showing a notification
			console.error('Create of org Invite failed:', error)
			toast.error('Create of org Invite failed')
		},
	})
}

export const useGetOrganizationRoles = <TData = IRole[], TError = unknown>(
	client: NextpeakClient,
	options?: Omit<UseQueryOptions<IRole[], TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IRole[], TError, TData>({
		queryKey: ['GetOrganizationRoles'],
		queryFn: () => client.getOrganizationRoles(),
		...opts,
	})
}

export const useGetOrganizationInvite = <TData = IInvite, TError = unknown>(
	client: NextpeakPublicClient,
	params: { inviteId: string },
	options?: Omit<UseQueryOptions<IInvite, TError, TData>, 'queryKey'>
) => {
	const { inviteId } = params
	const opts = options || {}
	return useQuery<IInvite, TError, TData>({
		queryKey: ['GetOrganizationInvite', inviteId],
		queryFn: () => client.getOrganizationInvite(inviteId),
		...opts,
	})
}

export const useGetUserOrganizationRolesV2 = (client: NextpeakClient, userId?: string) => {
	return useQuery<unknown, Error, IRole[]>({
		queryKey: ['GetUserOrganizationRoles', userId],
		queryFn: () => client.getUserOrganizationRolesV2(userId!),
		enabled: !!userId,
	})
}

export const useUpdateOrgInvite = (client: NextpeakPublicClient) => {
	return useMutation({
		mutationFn: ({ inviteId, inviteData }: { inviteId: string; inviteData: IInviteAction }) =>
			client.updateOrganizationInvite(inviteId, inviteData),
		onSuccess: (res) => {
			console.log(res)
			toast.success('Invite successfully updated')
		},
		onError: (error) => {
			// Optionally handle errors here, like showing a notification
			console.error(error)
			toast.error('Update of org Invite failed')
		},
	})
}

export const useUpdateUserOrganizationRoles = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) => client.updateUserOrganizationRoles(userId, roleIds),
		onSuccess: () => {
			toast.success('User roles updated')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Roles update failed')
		},
	})
}

export const useGetUserV2 = <TData = IUser, TError = unknown>(
	client: NextpeakClient,
	params: { userId: string; includes?: string[] },
	options?: Omit<UseQueryOptions<IUser, TError, TData>, 'queryKey'>
) => {
	const { userId, includes } = params
	const opts = options || {}
	return useQuery<IUser, TError, TData>({
		queryKey: ['GetUser', userId, includes],
		queryFn: () => client.getUserV2(userId, includes),
		...opts,
	})
}

export const useGetMeV2 = <TData = IUser, TError = unknown>(
	client: NextpeakClient,
	options?: Omit<UseQueryOptions<IUser, TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IUser, TError, TData>({ queryKey: ['GetMe'], queryFn: () => client.getMeV2(), ...opts })
}

export const usePromoteUser = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ userId, positionId }: { userId: string; positionId: string }) => client.promoteUser(userId, positionId),
		onSuccess: () => {
			toast.success('User promoted')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Promotion failed')
		},
	})
}

export const useUpdateUserManager = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ userId, newManagerId }: { userId: string; newManagerId: string }) => client.updateUserManager(userId, newManagerId),
		onSuccess: () => {
			toast.success('User promoted')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Promotion failed')
		},
	})
}

export const useUpdateUser = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ userId, updateData }: { userId: string; updateData: UpdateUserDto }) => client.updateUser(userId, updateData),
		onSuccess: () => {
			toast.success('User updated successfully')
		},
		onError: (error) => {
			console.error(error)
			toast.error('User update failed')
		},
	})
}

export const useGetOrganizationPublic = <TData = IPublicOrganization, TError = unknown>(
	client: NextpeakPublicClient,
	params: { organizationId: string },
	options?: Omit<UseQueryOptions<IPublicOrganization, TError, TData>, 'queryKey'>
) => {
	const { organizationId } = params
	const opts = options || {}
	return useQuery<IPublicOrganization, TError, TData>({
		queryKey: ['GetOrganizationPublic', organizationId],
		queryFn: () => client.getOrganizationPublic(organizationId),
		...opts,
	})
}

export const useGetFullTrajectory = <TData = IPositionTrajectory[], TError = unknown>(
	client: NextpeakClient,
	params: { path: string },
	options?: Omit<UseQueryOptions<IPositionTrajectory[], TError, TData>, 'queryKey'>
) => {
	const { path } = params
	const opts = options || {}
	return useQuery<IPositionTrajectory[], TError, TData>({
		queryKey: ['GetFullTrajectory', path],
		queryFn: () => client.getFullTrajectory(path),
		...opts,
	})
}

export const useGetOrganizationInvites = <TData = IInvite[], TError = unknown>(
	client: NextpeakClient,
	params: { organizationId: string },
	options?: Omit<UseQueryOptions<IInvite[], TError, TData>, 'queryKey'>
) => {
	const { organizationId } = params
	const opts = options || {}
	return useQuery<IInvite[], TError, TData>({
		queryKey: ['GetOrganizationInvites', organizationId],
		queryFn: () => client.getOrganizationInvites(organizationId),
		...opts,
	})
}

export const useGetPosition = <TData = IPosition, TError = unknown>(
	client: NextpeakClient,
	params: { positionId: string; includes?: string[] },
	options?: Omit<UseQueryOptions<IPosition, TError, TData>, 'queryKey'>
) => {
	const { positionId, includes } = params
	const opts = options || {}
	return useQuery<IPosition, TError, TData>({
		queryKey: ['GetPosition', positionId, includes],
		queryFn: () => client.getPosition(positionId, includes),
		...opts,
	})
}

export const useGetPositions = <TData = IPosition[], TError = unknown>(
	client: NextpeakClient,
	filters?: IPositionsFilters,
	options?: Omit<UseQueryOptions<IPosition[], TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IPosition[], TError, TData>({
		queryKey: ['GetPositions', filters],
		queryFn: () => client.getPositions(filters),
		...opts,
	})
}

export const useGetPaginatedAssessments = <TData = IPaginatedAssessmentsResponse, TError = unknown>(
	client: NextpeakClient,
	filters?: IPaginatedAssessmentFilters,
	options?: Omit<UseQueryOptions<IPaginatedAssessmentsResponse, TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IPaginatedAssessmentsResponse, TError, TData>({
		queryKey: ['GetPaginatedAssessments', filters],
		queryFn: () => client.getPaginatedAssessments(filters),
		...opts,
	})
}

export const useGetAssessments = <TData = IAssessment[], TError = unknown>(
	client: NextpeakClient,
	filters?: IAssessmentFilters,
	options?: Omit<UseQueryOptions<IAssessment[], TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IAssessment[], TError, TData>({
		queryKey: ['GetAssessments', filters],
		queryFn: () => client.getAssessments(filters),
		...opts,
	})
}

export const useGetAssessment = <TData = IAssessment, TError = unknown>(
	client: NextpeakClient,
	params: { assessmentId: string; includes?: string[] },
	options?: Omit<UseQueryOptions<IAssessment, TError, TData>, 'queryKey'>
) => {
	const { assessmentId, includes } = params
	const opts = options || {}
	return useQuery<IAssessment, TError, TData>({
		queryKey: ['GetAssessments', assessmentId, includes],
		queryFn: () => client.getAssessment(assessmentId, includes),
		...opts,
	})
}

export const useCreateAssessment = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: (input: ICreateAssessmentRequest) => client.createAssessment(input),
		onSuccess: () => {
			toast.success('Assessment created')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Assessment creation failed')
		},
	})
}

export const useCompleteAssessmentResponse = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: (assessmentId: string) => client.completeAssessmentResponse(assessmentId),
		onSuccess: () => {
			toast.success('Assessment response completed')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Assessment response completion failed')
		},
	})
}

export const useSaveAnswers = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ assessmentResponseId, answers }: { assessmentResponseId: string; answers: ISaveAnswersRequest }) =>
			client.saveAnswers(assessmentResponseId, answers),
		onSuccess: () => {
			toast.success('Assessment response completed')
		},
		onError: (error) => {
			console.error(error)
			toast.error('Assessment response completion failed')
		},
	})
}

export const useGetUserPositionAssessments = <TData = IAssessment[], TError = unknown>(
	client: NextpeakClient,
	params: { userId: string; filters: IUserPositionAssessmentFilters },
	options?: Omit<UseQueryOptions<IAssessment[], TError, TData>, 'queryKey'>
) => {
	const { userId, filters } = params
	const opts = options || {}
	return useQuery<IAssessment[], TError, TData>({
		queryKey: ['GetUserPositionAssessments', userId, filters],
		queryFn: () => client.getUserPositionAssessments(userId, filters),
		...opts,
	})
}

export const useGetRoles = <TData = IPositionRole[], TError = unknown>(
	client: NextpeakClient,
	options?: Omit<UseQueryOptions<IPositionRole[], TError, TData>, 'queryKey'>
) => {
	const opts = options || {}
	return useQuery<IPositionRole[], TError, TData>({
		queryKey: ['GetPositionRole'],
		queryFn: () => client.getRoles(),
		...opts,
	})
}

export const useGetAnswers = <TData = IAnswerForStats[], TError = unknown>(
	client: NextpeakClient,
	params: { filters: IGetAnswersFilter },
	options?: Omit<UseQueryOptions<IAnswerForStats[], TError, TData>, 'queryKey'>
) => {
	const { filters } = params
	const opts = options || {}
	return useQuery<IAnswerForStats[], TError, TData>({
		queryKey: ['GetAnswers', filters],
		queryFn: () => client.getAnswers(filters),
		...opts,
	})
}

export const useDeleteEmployee = (client: NextpeakClient) => {
	return useMutation({
		mutationFn: ({ userId, organizationId }: { userId: string; organizationId: string }) => {
			return client.deleteUserFromOrganization(organizationId, userId)
		},
		onSuccess: () => {
			toast.success('Employee deleted successfully')
		},
		onError: (error) => {
			console.error('Failed to delete employee:', error)
			toast.error('Failed to delete employee')
		},
	})
}
